/**
 * Lot F (Eric 2026-06-30) — Lisibilite des cles BYOK.
 *
 * Une cle BYOK illisible ne casse rien de visible : le resolveur retombe sur
 * la cle plateforme Knoria et l'organisation continue de fonctionner...
 * facturee a Knoria. Cette carte rend ce silence visible.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

export const MasterKeyHealthCard = ({ initial, awaiting }) => {
  // Tâches 1362 · 1363 (Eric 2026-06-30) — Cette carte était écrite sans
  // accents ET sans version anglaise.
  const { tr } = useLang();
  // L'appel groupe arrive APRES le premier rendu : `useState(initial)` figeait
  // donc « aucun controle effectue » pour toujours. On lit la prop, et l'etat
  // local ne sert qu'a un rechargement manuel.
  const [local, setLocal] = useState(null);
  const [loading, setLoading] = useState(false);
  const data = local || initial || null;

  const recharger = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/sentinel/master-key/health`, {
        withCredentials: true,
      });
      setLocal(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Controle indisponible");
    } finally {
      setLoading(false);
    }
  };

  const etat = data?.ok ? "ok" : data ? "alerte" : "inconnu";
  const illisibles = (data?.results || []).filter((r) => !r.decryptable);

  return (
    <div
      className="rounded-xl border border-border bg-card p-5 space-y-4"
      data-testid="master-key-health-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${
                etat === "ok" ? "bg-emerald-500" : etat === "alerte" ? "bg-red-500" : "bg-zinc-400"
              }`}
            />
            {tr("Clé maîtresse & clés clients", "Master key & client keys")}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {tr(
              "Vérifie que chaque clé BYOK se déchiffre encore. Une clé illisible ne provoque aucune erreur : l'organisation retombe sur la clé Knoria et son usage nous est facturé.",
              "Checks that every BYOK key can still be decrypted. An unreadable key raises no error: the organisation falls back on the Knoria key and we get billed for it.",
            )}
          </p>
        </div>
        <button
          onClick={recharger}
          disabled={loading}
          className="flex-shrink-0 px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-accent disabled:opacity-50"
          data-testid="master-key-health-refresh"
        >
          {loading ? tr("Contrôle…", "Checking…") : tr("Contrôler", "Check")}
        </button>
      </div>

      {!data && (
        <p className="text-sm text-muted-foreground" data-testid="master-key-health-empty">
          {awaiting ? tr("Chargement…", "Loading…") : tr("Aucun contrôle encore effectué.", "No check performed yet.")}
        </p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="master-key-health-stats">
            <Stat label={tr("Clés vérifiées", "Keys checked")} value={data.checked_total} />
            <Stat label={tr("Lisibles", "Readable")} value={data.decryptable_total} />
            <Stat
              label={tr("Illisibles", "Unreadable")}
              value={data.undecryptable_total}
              alerte={data.undecryptable_total > 0}
            />
            <Stat
              label={tr("Replis silencieux", "Silent fallbacks")}
              value={data.silent_fallback_total}
              alerte={data.silent_fallback_total > 0}
            />
          </div>

          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>
              {tr("Secret", "Secret")} <code>KNORIA_KEY_ENCRYPTION_KEY</code> :{" "}
              <strong className={data.env_key_present ? "text-emerald-600" : "text-red-600"}>
                {data.env_key_present ? tr("présent", "present") : tr("ABSENT", "MISSING")}
              </strong>
            </li>
            <li>
              {tr("Copie de secours en base :", "Backup copy in the database:")}{" "}
              <strong className={data.legacy_copy_in_db ? "text-amber-600" : "text-emerald-600"}>
                {data.legacy_copy_in_db ? tr("encore présente (faille 1201)", "still present (flaw 1201)") : tr("supprimée", "removed")}
              </strong>
            </li>
            {data.fernet_error ? (
              <li className="text-red-600">{tr("Clé maîtresse inutilisable :", "Master key unusable:")} {data.fernet_error}</li>
            ) : null}
          </ul>

          {illisibles.length > 0 && (
            <div
              className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2"
              data-testid="master-key-health-failures"
            >
              <p className="text-xs font-bold text-red-800">
                {tr(`${illisibles.length} clé(s) ne se déchiffrent plus`, `${illisibles.length} key(s) can no longer be decrypted`)}
              </p>
              {illisibles.map((r) => (
                <div
                  key={`${r.org_id}-${r.service}`}
                  className="text-xs text-red-900 flex flex-wrap gap-x-2"
                  data-testid={`master-key-failure-${r.service}`}
                >
                  <strong>{r.org_name}</strong>
                  <span>· {r.service}</span>
                  <span>· {r.detail}</span>
                  {r.silent_fallback && (
                    <span className="font-bold">· {tr("repli silencieux sur la clé Knoria", "silent fallback to the Knoria key")}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, alerte }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`text-xl font-bold ${alerte ? "text-red-600" : ""}`}>{value}</div>
  </div>
);

export default MasterKeyHealthCard;
